import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, ImageIcon } from "lucide-react";

function fullUrl(u) {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
}

export default function ImageUploader({ value, onChange, label = "Image", aspect = "16/9", testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || !files[0]) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 5 Mo).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image envoyée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const preview = fullUrl(value);

  return (
    <div data-testid={testid}>
      {label && <label className="block text-sm text-ink-soft mb-1.5">{label}</label>}
      <div
        className="relative rounded-2xl border border-dashed border-border bg-white hover:border-sage-400 transition cursor-pointer overflow-hidden"
        style={{ aspectRatio: aspect }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
      >
        {preview ? (
          <img src={preview} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-ink-mute">
            <div className="text-center px-4">
              <ImageIcon className="w-7 h-7 mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-sm">Cliquez ou glissez une image</div>
              <div className="text-xs text-ink-mute mt-0.5">PNG · JPG · WebP · max 5 Mo</div>
            </div>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-ink/50 grid place-items-center text-white">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          className="absolute bottom-3 right-3 btn-pill bg-white/95 text-ink px-4 py-2 text-xs shadow hover:bg-white"
        >
          <Upload className="w-3.5 h-3.5" /> {preview ? "Changer" : "Envoyer"}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
